import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ActionItem } from '@shared/types';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAsync } from '../lib/useAsync';
import { useToast } from '../lib/toast';
import { dateLabel, money, relativeTime } from '../lib/format';
import {
  ArchStatusTag,
  IssueStatusTag,
  LoadingPage,
  PriorityTag,
  Stat,
  Tag,
  ViolationStatusTag,
} from '../components/ui';
import { PageHeader } from '../components/PageHeader';

export function BoardHubPage() {
  const { canUseFinance } = useAuth();
  const summary = useAsync(async () => (await api.boardSummary()).summary, []);
  const actions = useAsync(async () => (await api.actionItems()).actionItems, []);

  if (summary.loading && !summary.data) return <LoadingPage label="Loading the board hub…" />;
  const data = summary.data;
  if (!data) return null;

  return (
    <div>
      <PageHeader
        title="Board Hub"
        lede="Everything the board is carrying right now — community feedback, review queue, the calendar, and where the money stands."
      />

      <div className="card">
        <div className="stat-grid">
          <Stat
            label="Open requests"
            value={data.issues.open + data.issues.inProgress}
            tone={data.issues.urgent > 0 ? 'bad' : undefined}
          />
          <Stat label="Urgent" value={data.issues.urgent} tone={data.issues.urgent > 0 ? 'bad' : undefined} />
          <Stat label="Reviews pending" value={data.architecture.pending} />
          <Stat label="Upcoming events" value={data.events.upcoming} />
          <Stat label="Open violations" value={data.violations.open} tone={data.violations.open > 0 ? 'warn' : undefined} />
          {data.finance && (
            <Stat
              label="Projected year end"
              value={money(data.finance.projectedYearEnd)}
              tone={data.finance.projectedYearEnd > data.finance.annualBudget ? 'warn' : 'ok'}
            />
          )}
          {data.finance && <Stat label="Delinquent" value={money(data.finance.delinquentTotal)} />}
        </div>
      </div>

      <div className="grid two">
        <div className="card">
          <div className="card-head">
            <h3>Community feedback</h3>
            <Link className="btn ghost tiny" to="/requests">
              Open all
            </Link>
          </div>
          <p className="mut">
            {data.issues.open} open · {data.issues.inProgress} in progress · {data.issues.resolved}{' '}
            resolved
          </p>
          <div className="stack" style={{ gap: 8, marginTop: 10 }}>
            {data.recentIssues.length === 0 && <span className="mut">Nothing reported yet.</span>}
            {data.recentIssues.map((issue) => (
              <div key={issue.id} className="row between" style={{ gap: 10 }}>
                <span>{issue.title}</span>
                <span className="tag-row">
                  <PriorityTag priority={issue.priority} />
                  <IssueStatusTag status={issue.status} />
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>Review queue</h3>
            <Link className="btn ghost tiny" to="/architecture">
              Open all
            </Link>
          </div>
          <p className="mut">
            {data.architecture.pending} pending · {data.architecture.approved} approved ·{' '}
            {data.architecture.denied} denied
          </p>
          <div className="stack" style={{ gap: 8, marginTop: 10 }}>
            {data.recentSubmissions.length === 0 && <span className="mut">No submissions yet.</span>}
            {data.recentSubmissions.map((item) => (
              <div key={item.id} className="row between" style={{ gap: 10 }}>
                <span>{item.title}</span>
                <ArchStatusTag status={item.status} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Violations</h3>
          <Link className="btn ghost tiny" to="/violations">
            Open all
          </Link>
        </div>
        <p className="mut">
          {data.violations.open} open · {data.violations.noticeSent} notice sent · {data.violations.resolved}{' '}
          closed
        </p>
        <div className="stack" style={{ gap: 8, marginTop: 10 }}>
          {data.recentViolations.length === 0 && <span className="mut">Nothing reported yet.</span>}
          {data.recentViolations.map((item) => (
            <div key={item.id} className="row between" style={{ gap: 10 }}>
              <span>{item.propertyAddress}</span>
              <ViolationStatusTag status={item.status} />
            </div>
          ))}
        </div>
      </div>

      <h2>Action items</h2>
      <ActionItems state={actions} />

      <h2>Recent activity</h2>
      <div className="card">
        {data.activity.length === 0 ? (
          <p className="mut">Nothing logged yet.</p>
        ) : (
          data.activity.map((entry) => (
            <div key={entry.id} className="row between" style={{ padding: '7px 0', gap: 12 }}>
              <span>{entry.summary}</span>
              <span className="mut nowrap">{relativeTime(entry.createdAt)}</span>
            </div>
          ))
        )}
      </div>

      <div className="card">
        <h3>Board tools</h3>
        <div className="btn-row">
          {canUseFinance && (
            <Link className="btn s" to="/financials">
              Financials
            </Link>
          )}
          <Link className="btn s" to="/newsletter">
            Draft the newsletter
          </Link>
          <Link className="btn s" to="/board/residents">
            Member Management
          </Link>
          <Link className="btn s" to="/community">
            Community Management
          </Link>
          <Link className="btn s" to="/violations">
            Violations
          </Link>
          <Link className="btn s" to="/architecture/guidelines">
            Guidelines
          </Link>
          <Link className="btn s" to="/bylaws">
            By Laws folder
          </Link>
        </div>
      </div>
    </div>
  );
}

function ActionItems({ state }: { state: ReturnType<typeof useAsync<ActionItem[]>> }) {
  const { fail } = useToast();
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [busy, setBusy] = useState(false);

  const items = state.data ?? [];
  const open = items.filter((item) => !item.done);
  const done = items.filter((item) => item.done);

  async function add() {
    if (!title.trim()) return;
    setBusy(true);
    try {
      const { actionItems } = await api.actionCreate({
        title: title.trim(),
        dueDate: dueDate || null,
      });
      state.setData(actionItems);
      setTitle('');
      setDueDate('');
    } catch (error) {
      fail(error, 'The action item was not added.');
    } finally {
      setBusy(false);
    }
  }

  async function toggle(item: ActionItem) {
    try {
      const { actionItems } = await api.actionUpdate(item.id, { done: !item.done });
      state.setData(actionItems);
    } catch (error) {
      fail(error, 'That change was not saved.');
    }
  }

  async function remove(item: ActionItem) {
    try {
      const { actionItems } = await api.actionDelete(item.id);
      state.setData(actionItems);
    } catch (error) {
      fail(error, 'The action item was not deleted.');
    }
  }

  return (
    <div className="card">
      {open.length === 0 && done.length === 0 && (
        <p className="mut">Nothing on the list. Add the first item below.</p>
      )}

      {open.map((item) => (
        <div key={item.id} className="row between" style={{ padding: '6px 0', gap: 10 }}>
          <label className="check" style={{ margin: 0 }}>
            <input type="checkbox" checked={false} onChange={() => void toggle(item)} />
            <span>
              {item.title}
              {item.assignedToName && <span className="mut"> · {item.assignedToName}</span>}
              {item.dueDate && (
                <span className={new Date(item.dueDate) < new Date() ? 'warn' : 'mut'}>
                  {' '}
                  · due {dateLabel(item.dueDate)}
                </span>
              )}
              {item.linkedType && <Tag>{item.linkedType === 'issue' ? 'Request' : 'Review'}</Tag>}
            </span>
          </label>
          <button type="button" className="btn ghost tiny" onClick={() => void remove(item)}>
            Remove
          </button>
        </div>
      ))}

      {done.length > 0 && (
        <>
          <hr className="divider" />
          <div className="label-text">Completed</div>
          {done.map((item) => (
            <label key={item.id} className="check">
              <input type="checkbox" checked readOnly onClick={() => void toggle(item)} />
              <span className="mut" style={{ textDecoration: 'line-through' }}>
                {item.title}
              </span>
            </label>
          ))}
        </>
      )}

      <hr className="divider" />
      <div className="row">
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void add();
          }}
          placeholder="New action item"
          style={{ flex: '2 1 240px', margin: 0 }}
        />
        <input
          type="date"
          value={dueDate}
          onChange={(event) => setDueDate(event.target.value)}
          style={{ flex: '1 1 150px', margin: 0 }}
          aria-label="Due date"
        />
        <button type="button" className="btn" disabled={busy || !title.trim()} onClick={() => void add()}>
          Add
        </button>
      </div>
    </div>
  );
}
